import { call } from '@advanza/api'
import { HoverText, Icon } from '@advanza/ui'
import clsx from 'clsx'
import Pagination from 'components/Pagination'
import React, { useEffect, useState } from 'react'

interface ProviderRow {
    service_provider_id: number
    business_name: string
    platforms: string[]
    device_count: number
    last_active: string | null
    active_24h: boolean
    active_7d: boolean
    active_30d: boolean
    notifications_sent: number
    notifications_shown: number
    notifications_clicked: number
    open_rate: number
    avg_open_time_seconds: number | null
    avg_response_time_minutes: number | null
}

interface ProvidersResponse {
    status?: string
    items?: ProviderRow[]
    count?: number
    page?: number
    pageSize?: number
    sortBy?: string
    sortDir?: string
    error?: string
}

type SortDir = 'asc' | 'desc'

type SortableColumn =
    | 'business_name'
    | 'device_count'
    | 'last_active'
    | 'notifications_sent'
    | 'notifications_shown'
    | 'notifications_clicked'
    | 'open_rate'
    | 'avg_open_time_seconds'
    | 'avg_response_time_minutes'

interface Props {
    start: string
    end: string
    refreshKey: number
}

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [25, 50, 100]

const formatSeconds = (s: number | null): string => {
    if (s === null) return '–'
    if (s < 60) return `${Math.round(s)}s`
    if (s < 3600) {
        const m = Math.floor(s / 60)
        const sec = Math.round(s % 60)
        return sec > 0 ? `${m}m ${sec}s` : `${m}m`
    }
    const h = Math.floor(s / 3600)
    const m = Math.round((s % 3600) / 60)
    return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const formatMinutes = (m: number | null): string => {
    if (m === null) return '–'
    if (m < 1) return '< 1m'
    if (m < 60) return `${Math.round(m)}m`
    const h = Math.floor(m / 60)
    const min = Math.round(m % 60)
    return min > 0 ? `${h}h ${min}m` : `${h}h`
}

interface ActivityDotProps {
    active: boolean
}

const ActivityDot = ({ active }: ActivityDotProps) => (
    <span
        className={clsx(
            'inline-block h-2.5 w-2.5 rounded-full',
            active ? 'bg-active-green' : 'bg-neutral-300',
        )}
        title={active ? 'Active' : 'Inactive'}
    />
)

interface SortIndicatorProps {
    active: boolean
    dir: SortDir
}

const SortIndicator = ({ active, dir }: SortIndicatorProps) => {
    if (!active) {
        return <Icon name="unfold_more" fontSize={14} />
    }
    return <Icon name={dir === 'asc' ? 'arrow_upward' : 'arrow_downward'} fontSize={14} />
}

const HEADER_ALIGN_CLASS = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
} as const

const HEADER_JUSTIFY_CLASS = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
} as const

interface SortableHeaderProps {
    column: SortableColumn
    label: string
    align?: 'left' | 'center' | 'right'
    info?: React.ReactNode
    sortBy: SortableColumn
    sortDir: SortDir
    onSort: (column: SortableColumn) => void
}

const SortableHeader = ({
    column,
    label,
    align = 'left',
    info,
    sortBy,
    sortDir,
    onSort,
}: SortableHeaderProps) => {
    const active = sortBy === column
    const alignClass = HEADER_ALIGN_CLASS[align]
    const justifyClass = HEADER_JUSTIFY_CLASS[align]
    return (
        <th
            scope="col"
            className={clsx(
                'px-4 py-3',
                alignClass,
                'font-semibold text-neutral-700 cursor-pointer select-none hover:bg-neutral-100 whitespace-nowrap',
            )}
            onClick={() => onSort(column)}>
            <span className={clsx('inline-flex items-center gap-1', justifyClass)}>
                {label}
                {info && (
                    <span
                        className="inline-flex cursor-help text-neutral-400 hover:text-neutral-600"
                        onClick={(e) => e.stopPropagation()}>
                        <HoverText trigger={<Icon name="info" fontSize={14} />}>{info}</HoverText>
                    </span>
                )}
                <SortIndicator active={active} dir={sortDir} />
            </span>
        </th>
    )
}

const NativeAppProviderTable = ({ start, end, refreshKey }: Props) => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [sortBy, setSortBy] = useState<SortableColumn>('business_name')
    const [sortDir, setSortDir] = useState<SortDir>('asc')
    const [items, setItems] = useState<ProviderRow[]>([])
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [previousRefreshKey, setPreviousRefreshKey] = useState(refreshKey)

    useEffect(() => {
        const bypassCache = refreshKey !== previousRefreshKey
        setPreviousRefreshKey(refreshKey)
        loadPage(page, pageSize, sortBy, sortDir, bypassCache)
    }, [page, pageSize, sortBy, sortDir, refreshKey, start, end])

    const loadPage = async (
        nextPage: number,
        nextPageSize: number,
        nextSortBy: SortableColumn,
        nextSortDir: SortDir,
        bypassCache: boolean,
    ) => {
        setLoading(true)
        setError('')
        try {
            const params = new URLSearchParams()
            params.set('page', String(nextPage))
            params.set('pageSize', String(nextPageSize))
            params.set('sortBy', nextSortBy)
            params.set('sortDir', nextSortDir)
            if (start) params.set('start', start)
            if (end) params.set('end', end)
            if (bypassCache) params.set('bypass_cache', '1')
            // Cache-buster: keeps the URL unique so no CDN/browser cache can
            // serve a stale body for this live dashboard.
            params.set('_', String(Date.now()))

            const url = `office/misc/native-app-analytics-providers?${params.toString()}`
            const response = (await call(url)) as ProvidersResponse
            if (response?.status === 'success' && Array.isArray(response.items)) {
                setItems(response.items)
                setCount(response.count ?? 0)
            } else {
                setError(response?.error || 'Failed to load providers')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const onSort = (column: SortableColumn) => {
        if (column === sortBy) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortBy(column)
            setSortDir('asc')
        }
        setPage(0)
    }

    const onPageChange = (nextPage: number) => {
        setPage(nextPage)
    }

    const onPageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const next = Number(event.target.value) || DEFAULT_PAGE_SIZE
        setPageSize(next)
        setPage(0)
    }

    return (
        <div className="space-y-3">
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                    {error}
                </div>
            )}

            <div className="relative max-h-[480px] overflow-y-auto overflow-x-auto rounded-lg border border-neutral-200">
                {loading && (
                    <div className="absolute inset-x-0 top-0 z-20 h-1 overflow-hidden bg-blue-100">
                        <div className="h-full w-1/3 animate-pulse bg-blue-500" />
                    </div>
                )}
                <table className="min-w-full divide-y divide-neutral-200 text-md">
                    <thead className="bg-neutral-50 sticky top-0 z-10">
                        <tr>
                            <SortableHeader
                                column="business_name"
                                label="Provider"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <th
                                scope="col"
                                className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Platform
                            </th>
                            <SortableHeader
                                column="device_count"
                                label="Devices"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="last_active"
                                label="Last active"
                                align="center"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <th
                                scope="col"
                                className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                24h
                            </th>
                            <th
                                scope="col"
                                className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                7d
                            </th>
                            <th
                                scope="col"
                                className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                30d
                            </th>
                            <SortableHeader
                                column="notifications_sent"
                                label="Sent"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="notifications_shown"
                                label="Shown"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="notifications_clicked"
                                label="Clicked"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="open_rate"
                                label="Open %"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="avg_open_time_seconds"
                                label="Avg open"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                            <SortableHeader
                                column="avg_response_time_minutes"
                                label="Avg response"
                                info="Average response time across all channels (not just native app)"
                                align="right"
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={onSort}
                            />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white">
                        {items.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={13}
                                    className="px-4 py-6 text-center text-md text-neutral-500">
                                    No native app providers found
                                </td>
                            </tr>
                        )}
                        {items.map((m) => (
                            <tr key={m.service_provider_id} className="hover:bg-neutral-50/80">
                                <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                                    {m.business_name}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700">
                                    {m.platforms.join(', ')}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.device_count}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700 whitespace-nowrap">
                                    {m.last_active
                                        ? new Date(m.last_active).toLocaleDateString()
                                        : '–'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <ActivityDot active={m.active_24h} />
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <ActivityDot active={m.active_7d} />
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <ActivityDot active={m.active_30d} />
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.notifications_sent}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.notifications_shown}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.notifications_clicked}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                                    {m.open_rate}%
                                </td>
                                <td className="px-4 py-3 text-right text-neutral-700">
                                    {formatSeconds(m.avg_open_time_seconds)}
                                </td>
                                <td className="px-4 py-3 text-right text-neutral-700">
                                    {formatMinutes(m.avg_response_time_minutes)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-md text-neutral-600">
                    Rows per page
                    <span className="relative inline-flex items-center">
                        <Icon
                            name="keyboard_arrow_down"
                            fontSize={18}
                            className="pointer-events-none absolute left-1 text-neutral-500"
                        />
                        <select
                            value={pageSize}
                            onChange={onPageSizeChange}
                            className="appearance-none rounded-md border border-neutral-300 bg-white pl-7 pr-2 py-1 text-md cursor-pointer hover:border-neutral-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </span>
                </label>
                <div className="min-w-[160px]">
                    <Pagination
                        current={page}
                        count={count}
                        pageSize={pageSize}
                        onPageChange={onPageChange}
                    />
                </div>
            </div>
        </div>
    )
}

export default NativeAppProviderTable
